import moment from 'moment'

// Pure helpers for the Collections screens. No React, no network: everything here is unit tested.

export const CURRENCY = 'GHS'

/** 580 -> "GHS 5.80". Cents are always integers; null/undefined show a dash. */
export function formatMoney(cents, currency) {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents)))
    return '-'
  const value = Number(cents) / 100
  return `${currency || CURRENCY} ${value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

/** 0.4837 -> "48.4%". null means "no resolved attempts yet", shown as a dash. */
export function formatPercent(rate) {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate)))
    return '-'
  return `${(Number(rate) * 100).toFixed(1)}%`
}

export function formatDateTime(value) {
  if (!value) return '-'
  const m = moment(value)
  return m.isValid() ? m.format('DD MMM YYYY HH:mm') : '-'
}

export function formatDate(value) {
  if (!value) return '-'
  const m = moment(value)
  return m.isValid() ? m.format('DD MMM YYYY') : '-'
}

/** Attempt statuses in the order users care about, with the MUI Chip colour for each. */
export const ATTEMPT_STATUS = {
  Succeeded: { label: 'Collected', color: 'success' },
  Failed: { label: 'Failed', color: 'error' },
  Dispatched: { label: 'In progress', color: 'info' },
  Unknown: { label: 'No answer', color: 'warning' },
  Skipped: { label: 'Skipped', color: 'default' }
}

export function attemptStatusMeta(status) {
  return ATTEMPT_STATUS[status] || { label: status || '-', color: 'default' }
}

export const FAILURE_LABELS = {
  insufficient_funds: 'Insufficient funds',
  mandate_invalid: 'Mandate invalid',
  customer_declined: 'Customer declined',
  limit_exceeded: 'Limit exceeded',
  timeout: 'No response / timeout',
  duplicate_reference: 'Duplicate reference',
  unknown: 'Unknown'
}

/**
 * What to show for a failed attempt. A known category reads well ("Insufficient funds"); "Unknown" tells the
 * person nothing, so when the category is unknown the reason the system recorded is shown instead.
 */
export function failureText(category, reason) {
  if ((!category || category === 'unknown') && reason) return reason
  return failureLabel(category)
}

export function failureLabel(category) {
  if (!category) return '-'
  return FAILURE_LABELS[category] || category
}

export const SKIP_LABELS = {
  held: 'Held',
  attempt_in_flight: 'Another attempt in progress',
  not_eligible: 'Not eligible',
  already_paid: 'Customer already paid'
}

export function skipLabel(reason) {
  if (!reason) return '-'
  if (SKIP_LABELS[reason]) return SKIP_LABELS[reason]
  return reason
}

export const TRIGGER_LABELS = {
  scheduled: 'Scheduled',
  retry: 'Retry',
  manual: 'Manual'
}

export function triggerLabel(trigger) {
  return TRIGGER_LABELS[trigger] || trigger || '-'
}

export function channelLabel(channel) {
  if (!channel) return '-'
  if (channel === 'hubtel') return 'Hubtel'
  if (channel === 'itc') return 'ITC'
  return channel
}

/** "full" -> "Full amount", "partial_50" -> "50% of the amount". */
export function phaseLabel(phase) {
  if (!phase) return '-'
  if (phase === 'full') return 'Full amount'
  if (phase === 'remainder') return 'Remainder'
  const match = /^partial_(\d+)$/.exec(phase)
  if (match) return `${match[1]}% of the amount`
  return phase
}

/** One line for an attempt's "what happens next". */
export function describeNextStep(attempt) {
  if (!attempt) return ''
  if (attempt.status === 'Skipped') return skipLabel(attempt.skip_reason)
  if (attempt.status === 'Succeeded') return 'Collected'
  if (attempt.next_retry_at) {
    return `Next try ${formatDateTime(attempt.next_retry_at)}${
      attempt.next_retry_amount_in_cents
        ? ` for ${formatMoney(attempt.next_retry_amount_in_cents)}`
        : ''
    }`
  }
  if (attempt.metadata && attempt.metadata.retry_stopped)
    return 'Retries stopped'
  return ''
}

/** Date-range presets for the dashboard. `to` is "now" so the figures are live. */
export function rangePresets(now) {
  const base = moment(now || undefined)
  return [
    {
      key: 'month',
      label: 'This month',
      from: base
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD'),
      to: base.clone().format('YYYY-MM-DD')
    },
    {
      key: '7d',
      label: 'Last 7 days',
      from: base
        .clone()
        .subtract(6, 'days')
        .format('YYYY-MM-DD'),
      to: base.clone().format('YYYY-MM-DD')
    },
    {
      key: '30d',
      label: 'Last 30 days',
      from: base
        .clone()
        .subtract(29, 'days')
        .format('YYYY-MM-DD'),
      to: base.clone().format('YYYY-MM-DD')
    },
    {
      key: 'lastmonth',
      label: 'Last month',
      from: base
        .clone()
        .subtract(1, 'month')
        .startOf('month')
        .format('YYYY-MM-DD'),
      to: base
        .clone()
        .subtract(1, 'month')
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  ]
}

export function upcomingPresets(now) {
  const base = moment(now || undefined)
  return [
    {
      key: 'today',
      label: 'Today',
      from: base.format('YYYY-MM-DD'),
      to: base.format('YYYY-MM-DD')
    },
    {
      key: '7d',
      label: 'Next 7 days',
      from: base.format('YYYY-MM-DD'),
      to: base
        .clone()
        .add(7, 'days')
        .format('YYYY-MM-DD')
    },
    {
      key: '30d',
      label: 'Next 30 days',
      from: base.format('YYYY-MM-DD'),
      to: base
        .clone()
        .add(30, 'days')
        .format('YYYY-MM-DD')
    }
  ]
}

/**
 * Turns pasted text or a CSV into a clean list of identifiers: split on newlines, commas, semicolons,
 * tabs and spaces, trimmed, empties dropped, duplicates removed (first one wins, order kept). A header
 * row such as "msisdn" or "phone" is dropped.
 */
export function parseIdentifiers(text) {
  if (!text) return []
  const seen = {}
  const out = []
  String(text)
    .split(/[\s,;]+/)
    .map(s => s.replace(/^["']+|["']+$/g, '').trim())
    .filter(Boolean)
    .forEach(id => {
      const lowered = id.toLowerCase()
      if (
        [
          'msisdn',
          'phone',
          'phone_number',
          'mobile',
          'policy_guid',
          'customer_guid',
          'identifier',
          'number'
        ].indexOf(lowered) !== -1
      )
        return
      if (!seen[id]) {
        seen[id] = true
        out.push(id)
      }
    })
  return out
}

/** Escapes one CSV cell: quotes when it has a comma, quote or newline; doubles inner quotes. */
export function csvCell(value) {
  if (value === null || value === undefined) return ''
  const s = String(value)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** rows: array of objects. columns: [{ key, label, value?(row) }]. */
export function toCsv(rows, columns) {
  const header = columns.map(c => csvCell(c.label)).join(',')
  const lines = (rows || []).map(row =>
    columns.map(c => csvCell(c.value ? c.value(row) : row[c.key])).join(',')
  )
  return [header].concat(lines).join('\r\n')
}

export function downloadCsv(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/** The amount rule object the API expects, from the dialog's form fields. */
export function buildAmountRule(mode, value) {
  if (mode === 'percent') return { mode: 'percent', percent: Number(value) }
  if (mode === 'instalments') {
    return { mode: 'instalments', count: Number(value) }
  }
  if (mode === 'fixed') {
    return { mode: 'fixed', amount_in_cents: Math.round(Number(value) * 100) }
  }
  return { mode: 'full' }
}

/** Why a form value is unusable, or '' when it is fine. Mirrors the API's own checks. */
export function validateAmountRule(mode, value) {
  if (mode === 'percent') {
    const n = Number(value)
    if (!(n > 0 && n <= 100)) return 'Enter a percentage between 1 and 100'
  }
  if (mode === 'instalments') {
    const n = Number(value)
    if (!(Number.isInteger(n) && n >= 1))
      return 'Enter a whole number of instalments, 1 or more'
  }
  if (mode === 'fixed') {
    const n = Number(value)
    if (!(n > 0)) return 'Enter an amount greater than zero'
  }
  return ''
}

/** Server error text from an axios error, falling back to a generic message. */
export function errorMessage(error) {
  if (!error) return 'Something went wrong'
  const data = error.response && error.response.data
  if (data && data.message) {
    return Array.isArray(data.message)
      ? data.message.join(', ')
      : String(data.message)
  }
  if (data && data.error) return String(data.error)
  if (error.response && error.response.status === 403) {
    return 'You do not have permission to do this'
  }
  return error.message || 'Something went wrong'
}

/** The maximum number of days an upcoming view may span (the API computes dates per day). */
export const MAX_UPCOMING_DAYS = 120

export function clampUpcomingRange(from, to) {
  const f = moment(from)
  let t = moment(to)
  if (!f.isValid() || !t.isValid()) return { from, to, clamped: false }
  if (t.diff(f, 'days') > MAX_UPCOMING_DAYS) {
    t = f.clone().add(MAX_UPCOMING_DAYS, 'days')
    return {
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
      clamped: true
    }
  }
  return {
    from: f.format('YYYY-MM-DD'),
    to: t.format('YYYY-MM-DD'),
    clamped: false
  }
}

// ───────────── Segments: filter form <-> API criteria ─────────────

export const SEGMENT_STATUSES = [
  'Unpaid',
  'In_Arrears',
  'Non_Payment',
  'Full_Coverage',
  'Lapsed'
]

export const EMPTY_SEGMENT_FORM = {
  statuses: [],
  minDays: '',
  maxDays: '',
  minPeriods: '',
  minOwed: '',
  startFrom: '',
  startTo: '',
  lastPaidBefore: '',
  lastPaidAfter: '',
  neverPaid: false,
  channel: '',
  includeHeld: false
}

const toNumber = value => {
  if (value === '' || value === null || value === undefined) return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

/** Form state -> API criteria. Empty fields are left out; amounts are typed in GHS and sent as cents. */
export function formToCriteria(form) {
  const f = form || {}
  const c = {}
  if (f.statuses && f.statuses.length) c.policy_statuses = f.statuses
  const minDays = toNumber(f.minDays)
  const maxDays = toNumber(f.maxDays)
  const minPeriods = toNumber(f.minPeriods)
  const minOwed = toNumber(f.minOwed)
  if (minDays !== undefined) c.min_days_late = minDays
  if (maxDays !== undefined) c.max_days_late = maxDays
  if (minPeriods !== undefined) c.min_periods_behind = minPeriods
  if (minOwed !== undefined) c.min_arrears_in_cents = Math.round(minOwed * 100)
  if (f.startFrom) c.start_date_from = f.startFrom
  if (f.startTo) c.start_date_to = f.startTo
  if (f.lastPaidBefore) c.last_payment_before = f.lastPaidBefore
  if (f.lastPaidAfter) c.last_payment_after = f.lastPaidAfter
  if (f.neverPaid) c.never_paid = true
  if (f.channel) c.channel = f.channel
  if (f.includeHeld) c.include_held = true
  return c
}

/** API criteria -> form state (used when opening a saved segment). */
export function criteriaToForm(criteria) {
  const c = criteria || {}
  const text = v => (v === undefined || v === null ? '' : String(v))
  return {
    statuses: c.policy_statuses || [],
    minDays: text(c.min_days_late),
    maxDays: text(c.max_days_late),
    minPeriods: text(c.min_periods_behind),
    minOwed:
      c.min_arrears_in_cents === undefined
        ? ''
        : String(Number(c.min_arrears_in_cents) / 100),
    startFrom: text(c.start_date_from),
    startTo: text(c.start_date_to),
    lastPaidBefore: text(c.last_payment_before),
    lastPaidAfter: text(c.last_payment_after),
    neverPaid: !!c.never_paid,
    channel: text(c.channel),
    includeHeld: !!c.include_held
  }
}

/** Plain-language problems with the form, so the user sees them before the server says no. */
export function validateSegmentForm(form) {
  const f = form || {}
  const errors = []
  const minDays = toNumber(f.minDays)
  const maxDays = toNumber(f.maxDays)
  if (minDays !== undefined && minDays < 0)
    errors.push('Days late cannot be negative')
  if (minDays !== undefined && maxDays !== undefined && minDays > maxDays)
    errors.push('"At least" days late is more than "at most"')
  if (f.startFrom && f.startTo && f.startFrom > f.startTo)
    errors.push('Start date "from" is after "to"')
  const owed = toNumber(f.minOwed)
  if (owed !== undefined && owed < 0)
    errors.push('Amount owed cannot be negative')
  return errors
}

export const AWAITING_FIRST_PREMIUM = 'Awaiting first premium'

/** A policy that has never paid is not "on time", it is waiting for its first premium. */
export function isAwaitingFirstPremium(lateness) {
  if (!lateness || lateness.is_late) return false
  return (lateness.policy_status || lateness.status) === 'Unpaid'
}

/** "315 days" / "1 day" / "On time" / "Awaiting first premium" for a lateness object from the API. */
export function lateLabel(lateness) {
  if (!lateness) return '-'
  if (isAwaitingFirstPremium(lateness)) return AWAITING_FIRST_PREMIUM
  if (!lateness.is_late || !lateness.days_late) return 'On time'
  const d = lateness.days_late
  return d === 1 ? '1 day' : `${d} days`
}

/** Short summary of a lateness for the second line of a cell: "11 payments behind". */
export function behindLabel(lateness) {
  if (!lateness || !lateness.is_late) return ''
  const p = lateness.periods_behind
  if (!p) return ''
  return p === 1 ? '1 payment behind' : `${p} payments behind`
}

// ───────────── Scheduled collections ("promise to pay") ─────────────

export const MAX_SCHEDULE_DAYS = 30

/** One-click times for "the customer said they will pay in an hour". `now` is injectable for tests. */
export function schedulePresets(now) {
  const base = moment(now || undefined)
  const tomorrow = base
    .clone()
    .add(1, 'day')
    .startOf('day')
    .hour(9)
  return [
    { key: '1h', label: 'In 1 hour', at: base.clone().add(1, 'hour') },
    { key: '2h', label: 'In 2 hours', at: base.clone().add(2, 'hours') },
    { key: '4h', label: 'In 4 hours', at: base.clone().add(4, 'hours') },
    { key: 'tomorrow', label: 'Tomorrow 09:00', at: tomorrow }
  ]
}

/** moment -> the value a datetime-local input expects ("2026-10-10T15:30"). */
export function toInputValue(m) {
  return moment(m).format('YYYY-MM-DDTHH:mm')
}

/** Why the chosen time cannot be scheduled, or null. `value` is a datetime-local string. */
export function validateScheduleTime(value, now) {
  if (!value) return 'Choose a date and time'
  const at = moment(value)
  if (!at.isValid()) return 'That is not a valid date and time'
  const base = moment(now || undefined)
  if (at.isBefore(base.clone().add(1, 'minute')))
    return 'Choose a time in the future. Use "Collect now" for an immediate attempt'
  if (at.isAfter(base.clone().add(MAX_SCHEDULE_DAYS, 'days')))
    return `Choose a time within the next ${MAX_SCHEDULE_DAYS} days`
  return null
}

export const SCHEDULED_OUTCOME_LABELS = {
  attempt_started: 'Attempt started',
  already_collected: 'Skipped: already collected',
  held: 'Skipped: collection on hold',
  attempt_in_flight: 'Skipped: another attempt was in progress',
  not_eligible: 'Skipped: not eligible',
  cancelled: 'Cancelled',
  replaced: 'Replaced by a new time',
  collected: 'Cancelled: already collected'
}

/** "Pending" / "Attempt started" / "Skipped: already collected" for a scheduled collection row. */
export function scheduledStatusLabel(item) {
  if (!item) return '-'
  if (item.status === 'pending' || item.status === 'dispatching')
    return 'Waiting'
  if (item.status === 'failed') return `Failed: ${item.outcome || 'unknown'}`
  return SCHEDULED_OUTCOME_LABELS[item.outcome] || item.outcome || item.status
}

/** What a stored amount rule will collect from a debit order of `fullCents`: "GHS 10.40 (50%)". */
export function describeRuleShort(rule, fullCents) {
  if (!rule || rule.mode === 'full' || !rule.mode) return formatMoney(fullCents)
  if (rule.mode === 'percent') {
    const cents = Math.max(1, Math.round((fullCents * rule.percent) / 100))
    return `${formatMoney(cents)} (${rule.percent}%)`
  }
  return formatMoney(Math.min(rule.amount_in_cents, fullCents))
}

/** "in 42 minutes" / "in 3 hours" / "in 2 days" for a future time; "now" once it is due. */
export function untilLabel(value, now) {
  const at = moment(value)
  const base = moment(now || undefined)
  if (!at.isValid()) return ''
  const minutes = Math.round(at.diff(base, 'minutes', true))
  if (minutes <= 0) return 'now'
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`
  const days = Math.round(hours / 24)
  return `in ${days} days`
}

// ───────────── Links between Collections and the policy page ─────────────

/** Where a policy lives in the portal. Collections only knows the policy number once lateness is available. */
export function policyPath(policyNumber) {
  return policyNumber ? `/admin/policy-processing/${policyNumber}` : null
}

export function orderPath(debitOrderGuid) {
  return `/admin/collections/order/${debitOrderGuid}`
}

/** Colour and words for a policy status chip: "Non_Payment" -> "Non Payment". */
export function policyStatusMeta(status) {
  const label = status ? String(status).replace(/_/g, ' ') : '-'
  const colors = {
    Full_Coverage: 'success',
    In_Arrears: 'warning',
    Non_Payment: 'error',
    Lapsed: 'error',
    Cancelled: 'error',
    Unpaid: 'default'
  }
  return { label, color: colors[status] || 'default' }
}

// ───────────── Dashboard: comparisons, attention list, click-through paths ─────────────

/** "▲ 12% vs last period" for an amount or count. goodWhen: which direction is an improvement. */
export function changeLabel(current, previous, goodWhen) {
  if (current === null || current === undefined) return null
  if (previous === null || previous === undefined) return null
  if (Number(previous) === 0) {
    return Number(current) === 0
      ? null
      : { text: 'new this period', tone: 'neutral' }
  }
  const pct = ((Number(current) - Number(previous)) / Math.abs(previous)) * 100
  if (Math.abs(pct) < 0.5)
    return { text: 'same as last period', tone: 'neutral' }
  const up = pct > 0
  const improved = goodWhen === 'down' ? !up : up
  return {
    text: `${up ? '▲' : '▼'} ${Math.abs(pct).toFixed(0)}% vs last period`,
    tone: improved ? 'good' : 'bad'
  }
}

/** Same for a rate (0..1), in percentage points: "▲ 3.2 pts vs last period". */
export function pointChangeLabel(current, previous, goodWhen) {
  if (current === null || current === undefined) return null
  if (previous === null || previous === undefined) return null
  const pts = (Number(current) - Number(previous)) * 100
  if (Math.abs(pts) < 0.05)
    return { text: 'same as last period', tone: 'neutral' }
  const up = pts > 0
  const improved = goodWhen === 'down' ? !up : up
  return {
    text: `${up ? '▲' : '▼'} ${Math.abs(pts).toFixed(1)} pts vs last period`,
    tone: improved ? 'good' : 'bad'
  }
}

const query = params => {
  const parts = Object.keys(params || {})
    .filter(
      k => params[k] !== undefined && params[k] !== null && params[k] !== ''
    )
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

/** Attempts list, pre-filtered. The cards on the dashboard link here so the list adds up to the card. */
export function attemptsPath(filters) {
  return `/admin/collections/attempts${query(filters)}`
}

export function upcomingPath(filters) {
  return `/admin/collections/upcoming${query(filters)}`
}

export function atRiskPath(filters) {
  return `/admin/collections/at-risk${query(filters)}`
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

/**
 * The "needs attention now" list. Only things that are non-zero appear, most urgent first. `action: 'review-due'`
 * opens the preview of due retries instead of navigating.
 */
export function attentionItems(a) {
  if (!a) return []
  const items = []
  if (a.due_retries && a.due_retries.count > 0) {
    items.push({
      key: 'due',
      severity: 'high',
      title: `${plural(
        a.due_retries.count,
        'retry is',
        'retries are'
      )} due now`,
      detail: `${formatMoney(
        a.due_retries.amount_in_cents
      )} waiting to be sent`,
      action: 'review-due',
      actionLabel: 'Review and send'
    })
  }
  if (
    a.due_retries &&
    a.due_retries.count === 0 &&
    a.due_retries.held + a.due_retries.in_progress > 0
  ) {
    const held = a.due_retries.held
    const busy = a.due_retries.in_progress
    items.push({
      key: 'due-blocked',
      severity: 'info',
      title: `${plural(
        held + busy,
        'retry is',
        'retries are'
      )} due but held back`,
      detail: `${held ? `${held} on hold` : ''}${held && busy ? ', ' : ''}${
        busy ? `${busy} already in progress` : ''
      }. Nothing to send.`,
      path: upcomingPath({ kind: 'retry' }),
      actionLabel: 'Open'
    })
  }
  if (a.promises_overdue && a.promises_overdue.count > 0) {
    items.push({
      key: 'overdue',
      severity: 'high',
      title: `${plural(
        a.promises_overdue.count,
        'scheduled collection has',
        'scheduled collections have'
      )} not run on time`,
      detail: 'The scheduler may be behind. Check the service.',
      path: upcomingPath({ kind: 'promise' }),
      actionLabel: 'Open'
    })
  }
  if (a.unanswered_over_30_min && a.unanswered_over_30_min.count > 0) {
    items.push({
      key: 'unanswered',
      severity: 'medium',
      title: `${plural(
        a.unanswered_over_30_min.count,
        'attempt has',
        'attempts have'
      )} waited over 30 minutes for the partner`,
      detail: 'No reply yet. They will be marked unknown soon.',
      path: attemptsPath({ status: 'Dispatched' }),
      actionLabel: 'Open'
    })
  }
  if (a.at_risk && a.at_risk.orders > 0) {
    items.push({
      key: 'at-risk',
      severity: 'medium',
      title: `${plural(
        a.at_risk.orders,
        'debit order is',
        'debit orders are'
      )} failing`,
      detail: `${formatMoney(
        a.at_risk.amount_in_cents
      )} at risk, one instalment each`,
      path: atRiskPath(),
      actionLabel: 'Work the list'
    })
  }
  if (a.unowned_at_risk && a.unowned_at_risk.customers > 0) {
    items.push({
      key: 'no-lead',
      severity: 'medium',
      title: `${plural(
        a.unowned_at_risk.customers,
        'failing customer has',
        'failing customers have'
      )} no lead`,
      detail: `${formatMoney(
        a.unowned_at_risk.amount_in_cents
      )} at risk with nobody responsible for it`,
      path: teamPath(),
      actionLabel: 'Allocate'
    })
  }
  if (a.promises_next_24h && a.promises_next_24h.count > 0) {
    items.push({
      key: 'promises',
      severity: 'info',
      title: `${plural(
        a.promises_next_24h.count,
        'promised collection',
        'promised collections'
      )} in the next 24 hours`,
      detail: a.promises_next_24h.next_at
        ? `Next at ${formatDateTime(a.promises_next_24h.next_at)}`
        : '',
      path: upcomingPath({ kind: 'promise' }),
      actionLabel: 'Open'
    })
  }
  if (a.holds_ending_within_7_days && a.holds_ending_within_7_days.count > 0) {
    items.push({
      key: 'holds',
      severity: 'info',
      title: `${plural(
        a.holds_ending_within_7_days.count,
        'hold ends',
        'holds end'
      )} this week`,
      detail: 'Collection resumes automatically when they end.',
      path: '/admin/collections/holds',
      actionLabel: 'Open'
    })
  }
  if (
    a.failures_without_a_reason_7d &&
    a.failures_without_a_reason_7d.count > 0
  ) {
    items.push({
      key: 'no-reason',
      severity: 'info',
      title: `${plural(
        a.failures_without_a_reason_7d.count,
        'failure has',
        'failures have'
      )} no clear reason (last 7 days)`,
      detail: 'Open them to read what the system recorded.',
      path: attemptsPath({ status: 'Failed', category: 'unknown' }),
      actionLabel: 'Open'
    })
  }
  const rank = { high: 0, medium: 1, info: 2 }
  return items.sort((x, y) => rank[x.severity] - rank[y.severity])
}

/** What a card means, in words a non-technical person can use. Shown behind the (i) on each card. */
export const KPI_HELP = {
  collected: 'Money successfully collected from attempts sent in this period.',
  recovery:
    'Of the debit orders that failed in this period, the share of their money collected afterwards. Each order counts as one instalment, however many times it failed.',
  atRisk:
    'Active debit orders whose latest attempt failed and has not been collected since. One instalment per order.',
  success:
    'Collected attempts as a share of attempts that got an answer. Every attempt counts, including retries.',
  retry:
    'Debit orders with a retry scheduled for later. They are sent automatically at the time shown.',
  scheduled:
    'Regular debits, retries and promised collections due in the next 7 days.',
  hold:
    'Debit orders paused on purpose. Nothing is collected from them until the hold ends.',
  waiting:
    'Attempts sent to the partner that have not had a reply yet. Most answer within a minute or two.'
}

// ───────────── Work allocation: leads, stages, contact outcomes ─────────────

/** The pipeline an agent moves a customer through, in order. */
export const STAGES = [
  { key: 'new', label: 'Not contacted yet', color: 'default' },
  { key: 'attempted', label: 'Tried, no answer', color: 'warning' },
  { key: 'contacted', label: 'Spoke to them', color: 'info' },
  { key: 'promised', label: 'Promised to pay', color: 'primary' },
  { key: 'collected', label: 'Collected', color: 'success' },
  { key: 'refused', label: 'Refused', color: 'error' },
  { key: 'unreachable', label: 'Wrong or dead number', color: 'error' }
]

export function stageMeta(stage) {
  const found = STAGES.filter(s => s.key === stage)[0]
  return found || { key: stage, label: stage || '-', color: 'default' }
}

/** What an agent records after a call. */
export const OUTCOMES = [
  { value: 'no_answer', label: 'Called, no answer' },
  { value: 'spoke', label: 'Spoke to them' },
  { value: 'promised', label: 'Promised to pay' },
  { value: 'refused', label: 'Refused to pay' },
  { value: 'wrong_number', label: 'Wrong or dead number' },
  { value: 'paid', label: 'Says they have paid' },
  { value: 'note', label: 'Just a note' }
]

export function outcomeLabel(value) {
  const found = OUTCOMES.filter(o => o.value === value)[0]
  if (found) return found.label
  if (value === 'assigned') return 'Assigned'
  if (value === 'reassigned') return 'Moved to another lead'
  return value || '-'
}

/** A Keycloak user from the partner's user list, as the agent the allocation will carry. */
export function agentFromUser(user) {
  if (!user) return null
  const full = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return { id: user.id, name: full || user.username || user.email || user.id }
}

/**
 * The agents a team can hand work to, not just the ones already holding some. The backend only reports people an
 * allocation has already touched, so an agent nobody has given work to is invisible there. Folding the partner's
 * roster in keeps "nothing has been handed out yet" distinct from "there are no agents".
 */
export function withRoster(worked, roster) {
  const rows = (worked || []).slice()
  const known = new Set(rows.map(a => a.id))
  const fresh = (roster || []).filter(a => a && a.id && !known.has(a.id))
  fresh.forEach(a => {
    rows.push({
      id: a.id,
      name: a.name,
      customers: 0,
      by_stage: {},
      at_risk_customers: 0,
      at_risk_amount_in_cents: 0,
      collected_in_cents: 0,
      neglected: 0,
      promises_waiting: 0,
      last_activity_at: null
    })
  })
  return rows.sort(
    (a, b) => b.customers - a.customers || a.name.localeCompare(b.name)
  )
}

/** Plain words for who has no lead: "41 of 61 customers have a lead (67%)". */
export function coverageText(cov, noun) {
  if (!cov || !cov.customers) return `No ${noun || 'customers'} to cover`
  const pct = Math.round((cov.rate || 0) * 100)
  return `${cov.owned} of ${cov.customers} ${noun ||
    'customers'} have a lead (${pct}%)`
}

/** Colour for a coverage figure: a gap is something to fix, not just a number. */
export function coverageTone(cov) {
  if (!cov || !cov.customers) return undefined
  if (cov.unowned === 0) return 'good'
  return cov.rate < 0.8 ? 'bad' : 'warn'
}

/** The sentence under the preview in the allocate dialog. */
export function allocationSentence(preview) {
  if (!preview) return ''
  if (preview.to_assign === 0) {
    return preview.already_owned
      ? `All ${preview.customers} customers already have a lead.`
      : 'No customers match.'
  }
  const parts = [
    `${preview.to_assign} customer${
      preview.to_assign === 1 ? '' : 's'
    }, ${formatMoney(preview.amount_in_cents)} of instalments, will get a lead.`
  ]
  if (preview.already_owned)
    parts.push(
      `${preview.already_owned} already ${
        preview.already_owned === 1 ? 'has' : 'have'
      } one and ${preview.already_owned === 1 ? 'stays' : 'stay'} where ${
        preview.already_owned === 1 ? 'they are' : 'they are'
      }.`
    )
  if (preview.moving_from_others)
    parts.push(
      `${preview.moving_from_others} will be moved from their current lead.`
    )
  return parts.join(' ')
}

export function teamPath() {
  return '/admin/collections/team'
}

export function myQueuePath(filters) {
  return `/admin/collections/my-queue${query(filters)}`
}

/**
 * What a payment will do to a customer who is behind, in words an agent can read to the customer. Built from the
 * preview row the server sends (lateness, and complex's answer about restarting coverage). Empty when there is
 * nothing to say.
 */
export function coverageNote(item) {
  if (!item || !item.lateness || !item.lateness.is_late) return null
  const l = item.lateness
  const behind = `${l.periods_behind} payment${
    l.periods_behind === 1 ? '' : 's'
  }`
  const owes = l.arrears_in_cents ? ` (${formatMoney(l.arrears_in_cents)})` : ''
  const head = `${l.policy_number || 'This policy'} is ${behind} behind${owes}.`
  const c = item.coverage_reset
  if (!c) return { severity: 'info', text: head }
  if (c.eligible) {
    const keptInCents = c.catch_up_periods * (l.period_premium_in_cents || 0)
    const remaining = Math.max(
      keptInCents - (item.attempt_amount_in_cents || 0),
      0
    )
    const owed = `Only the latest ${c.catch_up_periods} payment${
      c.catch_up_periods === 1 ? '' : 's'
    } (${formatMoney(keptInCents)}) ${
      c.catch_up_periods === 1 ? 'is' : 'are'
    } owed; the ${c.periods_written_off} earlier ${
      c.periods_written_off === 1 ? 'payment is' : 'payments are'
    } no longer owed.`
    const after =
      remaining === 0
        ? 'This payment covers what is owed, so they are up to date.'
        : `After this payment ${formatMoney(remaining)} is still owed.`
    return {
      severity: 'warning',
      text: `${head} When this payment arrives, their coverage restarts from ${formatDate(
        c.new_due_date
      )}. ${owed} ${after}`
    }
  }
  if (c.reason && /covers everything/.test(c.reason)) {
    return {
      severity: 'success',
      text: `${head} This payment covers everything that is overdue.`
    }
  }
  return { severity: 'info', text: head }
}
